import * as React from 'react';
import { observer } from 'mobx-react';

import { UitkText } from 'uitk-react-text';

import { BaseTravelerOptions, SearchFormOptionsDialog, SearchFormOptionsDialogWrapper } from '.';
import { FlightsSearchFormTravelersOption } from 'types/search/wizard';
import { trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';
import { InteractableFieldComponentProps } from 'types/shared';
import { trackTravelersOptions } from 'utility/traveler-option-utils';

export const TravelerOptionsDialog = observer(({ onFieldInteraction }: InteractableFieldComponentProps) => {
  const { searchFormStore, analytics, routeStore } = useStores();
  const travelerOptions = searchFormStore.getSearchOption(
    'FlightsSearchFormTravelersOption'
  ) as FlightsSearchFormTravelersOption;

  const {
    __typename,
    doneAction,
    fieldIcon,
    label,
    travelerLimitLabel,
    doneActionAccessibility,
    cancelActionAccessibility
  } = travelerOptions;
  const dialogId = `${__typename}-dialog`;

  const revertChanges = () => {
    searchFormStore.revertToInitialTravelerOptions();
    trackAnalytics(travelerOptions.cancelAnalytics, analytics);
  };

  const setInitialTravelerOptions = () => {
    searchFormStore.setInitialTravelerOptions();
    trackAnalytics(travelerOptions.openAnalytics, analytics);
  };

  const onOpenDialog = () => {
    onFieldInteraction && onFieldInteraction();
    setInitialTravelerOptions();
  };

  const closeNestedDialog = () => {
    routeStore.closeDialog('pwaDialogNested');
  };

  const submitChanges = () => {
    travelerOptions.currentErrors = [];
    if (searchFormStore.areTravelerInputsValid(analytics)) {
      searchFormStore.cleanUpInitialTravelersOptions();
      trackAnalytics(travelerOptions.applyAnalytics, analytics);
      trackTravelersOptions(travelerOptions, analytics);
      closeNestedDialog();
    } else {
      searchFormStore.focusErrorSummaryInTravelers = true;
    }
  };

  const dismissDialog = () => {
    revertChanges();
    travelerOptions.currentErrors = [];
    closeNestedDialog();
  };

  return (
    <SearchFormOptionsDialogWrapper
      dialogId={dialogId}
      label={searchFormStore.travelersLabel}
      subLabel={label}
      labelIcon={fieldIcon.id}
    >
      <SearchFormOptionsDialog
        key={dialogId}
        dialogId={dialogId}
        dismissDialogAction={dismissDialog}
        doneAction={doneAction}
        doneButtonClick={submitChanges}
        openCallback={onOpenDialog}
        doneActionAccessibility={doneActionAccessibility}
        cancelActionAccessibility={cancelActionAccessibility}
      >
        <UitkText key={`${dialogId}-limit-label`} spacing="six" className="all-b-margin-four all-l-padding-three">
          {travelerLimitLabel}
        </UitkText>
        <form key={`${dialogId}-travelers-form`} className="all-x-margin-three all-t-margin-three all-b-margin-six">
          <fieldset key={`${dialogId}-fieldset`}>
            <legend
              className="uitk-type-heading-400 s-t-padding-two m-t-padding-two s-b-padding-three m-b-padding-three"
              key={`${dialogId}-legend`}
            >
              {label}
            </legend>
            <BaseTravelerOptions travelerOptions={travelerOptions} key={`${dialogId}-options`} />
          </fieldset>
        </form>
      </SearchFormOptionsDialog>
    </SearchFormOptionsDialogWrapper>
  );
});
